/**
 * Help overlay that shows context-aware controls for each game layer.
 * Parkington (top) gets corporate blue theme, Formicarium (bottom) gets gold/brown.
 */

interface HelpEntry {
  key: string;
  description: string;
}

interface HelpSection {
  title: string;
  entries: HelpEntry[];
}

const PARKINGTON_HELP: HelpSection[] = [
  {
    title: 'Tools',
    entries: [
      { key: 'S', description: 'Select tool' },
      { key: 'P', description: 'Precision brush (1x1)' },
      { key: 'H', description: 'Highway brush (3x3)' },
      // Listed under Tools, which is where a player will look for it, and worded
      // the way the tooltip words it. Nothing here says what it is *for*.
      { key: 'W', description: 'Withdraw staffing (over a federal parcel)' },
    ],
  },
  {
    title: 'Navigation',
    entries: [
      { key: 'Scroll', description: 'Zoom in/out' },
      { key: 'Space', description: 'Toggle zoom' },
      { key: 'Click+Drag', description: 'Paint terrain' },
      { key: 'Esc', description: 'Deselect' },
    ],
  },
  {
    title: 'Views',
    entries: [
      { key: 'Tab', description: 'Cycle view mode' },
      // Listed under Views rather than Tools, because it shows something rather
      // than doing something. Worded as the card titles itself.
      { key: 'R', description: 'Last Season Report' },
      { key: '?', description: 'Toggle this help' },
    ],
  },
];

const FORMICARIUM_HELP: HelpSection[] = [
  {
    title: 'Colony Selection',
    entries: [
      { key: '1 / 2 / 3', description: 'Select colony' },
      { key: 'Click tabs', description: 'Switch colony' },
    ],
  },
  {
    title: 'Colony Menu',
    entries: [
      { key: 'Save', description: 'Download colony state' },
      { key: 'Load', description: 'Load saved colony' },
      { key: 'Restart', description: 'Reset colony' },
    ],
  },
  {
    title: 'Navigation',
    entries: [
      { key: 'Scroll', description: 'Zoom in/out' },
      { key: 'Space', description: 'Toggle zoom' },
      { key: 'M', description: 'Toggle pan mode' },
      { key: 'Drag (pan mode)', description: 'Move viewport' },
      { key: 'Pinch', description: 'Touch zoom + pan' },
    ],
  },
  {
    title: 'Views',
    entries: [
      { key: 'Tab', description: 'Cycle view mode' },
      { key: '?', description: 'Toggle this help' },
    ],
  },
];

// Theme colors
const PARKINGTON_THEME = {
  accent: '#2d5a7b',
  accentLight: 'rgba(45, 90, 123, 0.3)',
  badgeBg: 'rgba(45, 90, 123, 0.8)',
  badgeText: '#fff',
  title: '#2d5a7b',
  text: '#e8e8e8',
  dimText: 'rgba(200, 200, 200, 0.7)',
  panelBg: 'rgba(20, 30, 40, 0.92)',
  panelBorder: 'rgba(45, 90, 123, 0.6)',
  sectionDivider: 'rgba(45, 90, 123, 0.3)',
};

const FORMICARIUM_THEME = {
  accent: '#c8a84e',
  accentLight: 'rgba(200, 168, 78, 0.3)',
  badgeBg: 'rgba(140, 110, 50, 0.8)',
  badgeText: '#fff',
  title: '#c8a84e',
  text: '#e8e8e8',
  dimText: 'rgba(200, 180, 140, 0.7)',
  panelBg: 'rgba(30, 22, 15, 0.92)',
  panelBorder: 'rgba(200, 168, 78, 0.5)',
  sectionDivider: 'rgba(200, 168, 78, 0.3)',
};

export class HelpOverlay {
  private visible = false;
  private opacity = 0;
  private targetOpacity = 0;
  private readonly FADE_SPEED = 6; // opacity units per second

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    this.visible = true;
    this.targetOpacity = 1;
  }

  hide(): void {
    this.targetOpacity = 0;
    // visible stays true until fade completes
  }

  isVisible(): boolean {
    return this.visible;
  }

  update(dt: number): void {
    if (this.opacity < this.targetOpacity) {
      this.opacity = Math.min(this.targetOpacity, this.opacity + this.FADE_SPEED * dt);
    } else if (this.opacity > this.targetOpacity) {
      this.opacity = Math.max(this.targetOpacity, this.opacity - this.FADE_SPEED * dt);
      if (this.opacity <= 0) {
        this.visible = false;
        this.opacity = 0;
      }
    }
  }

  renderOnTop(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (!this.visible) return;
    this.renderPanel(ctx, w, h, PARKINGTON_HELP, PARKINGTON_THEME, 'PARKINGTON CONTROLS');
  }

  renderOnBottom(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (!this.visible) return;
    this.renderPanel(ctx, w, h, FORMICARIUM_HELP, FORMICARIUM_THEME, 'FORMICARIUM CONTROLS');
  }

  private renderPanel(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    sections: HelpSection[],
    theme: typeof PARKINGTON_THEME,
    title: string,
  ): void {
    ctx.save();
    ctx.globalAlpha = this.opacity;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, 0, w, h);

    // Calculate panel dimensions
    const font = '"Segoe UI", system-ui, sans-serif';
    const panelW = Math.min(420, w - 40);
    const lineHeight = 28;
    const sectionGap = 10;
    const titleHeight = 40;
    const padY = 18;
    const footerHeight = 30;

    // Count total lines to compute height
    let totalLines = 0;
    for (const section of sections) {
      totalLines += 1; // section title
      totalLines += section.entries.length;
    }
    const panelH = titleHeight + padY * 2 + totalLines * lineHeight + (sections.length - 1) * sectionGap + footerHeight;

    const panelX = (w - panelW) / 2;
    const panelY = (h - panelH) / 2;

    // Panel background
    ctx.fillStyle = theme.panelBg;
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, 8);
    ctx.fill();

    // Panel border
    ctx.strokeStyle = theme.panelBorder;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Title
    ctx.fillStyle = theme.title;
    ctx.font = `bold 15px ${font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, w / 2, panelY + titleHeight / 2 + 2);

    // Title underline
    ctx.strokeStyle = theme.sectionDivider;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(panelX + 20, panelY + titleHeight);
    ctx.lineTo(panelX + panelW - 20, panelY + titleHeight);
    ctx.stroke();

    // Draw sections
    let curY = panelY + titleHeight + padY;
    const keyColX = panelX + 24;
    const descColX = panelX + 130;

    for (let si = 0; si < sections.length; si++) {
      const section = sections[si];

      // Section title
      ctx.fillStyle = theme.accent;
      ctx.font = `bold 12px ${font}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(section.title, keyColX, curY + lineHeight / 2);
      curY += lineHeight;

      // Entries
      for (const entry of section.entries) {
        // Key badge
        this.drawKeyBadge(ctx, keyColX, curY, entry.key, theme, font);

        // Description
        ctx.fillStyle = theme.text;
        ctx.font = `13px ${font}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(entry.description, descColX, curY + lineHeight / 2);
        curY += lineHeight;
      }

      // Section divider (except last)
      if (si < sections.length - 1) {
        curY += sectionGap / 2;
        ctx.strokeStyle = theme.sectionDivider;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(panelX + 20, curY);
        ctx.lineTo(panelX + panelW - 20, curY);
        ctx.stroke();
        curY += sectionGap / 2;
      }
    }

    // Footer
    ctx.fillStyle = theme.dimText;
    ctx.font = `11px ${font}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Press ? or Esc to close', w / 2, panelY + panelH - footerHeight / 2);

    ctx.restore();
  }

  private drawKeyBadge(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    key: string,
    theme: typeof PARKINGTON_THEME,
    font: string,
  ): void {
    ctx.font = `bold 11px ${font}`;
    const textW = ctx.measureText(key).width;
    const padX = 8;
    const badgeW = textW + padX * 2;
    const badgeH = 20;
    const badgeY = y + (28 - badgeH) / 2;

    // Badge background
    ctx.fillStyle = theme.badgeBg;
    ctx.beginPath();
    ctx.roundRect(x, badgeY, badgeW, badgeH, 4);
    ctx.fill();

    // Badge border
    ctx.strokeStyle = theme.accent;
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Badge text
    ctx.fillStyle = theme.badgeText;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(key, x + badgeW / 2, badgeY + badgeH / 2);
  }
}
